import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import moment from 'moment';
import { Moment } from 'moment';
import { SelectOneComponent } from '../../../../shared/components/select-one/select-one.component';
import { FormFieldComponent } from '../../../../shared/components/form-field/form-field.component';
import { DatePickerPadraoComponent } from '../../../../shared/components/date-picker-padrao/date-picker-padrao.component';
import { DiasSemanaComponent } from '../../../../shared/components/dias-semana/dias-semana.component';
import { DiaSemana } from '../../../../shared/model/horario-funcionamento/horario-funcionamento.model';
import { ProgramacaoDisparoModel } from '../../../../shared/model/disparo-automatico/programacao-disparo.model';
import {
  DIAS_SEMANA_MES,
  FREQUENCIAS,
  FrequenciaRecorrencia,
  OpcaoSegmento,
  POSICOES_SEMANA_MES,
  PosicaoSemanaMes,
  TERMINOS,
  TIPO_RECORRENCIA_MENSAL,
  TipoRecorrenciaMensal,
  TipoTerminoRecorrencia,
  UNIDADES_PERSONALIZADA,
  UnidadePersonalizada
} from '../../../../shared/model/disparo-automatico/recorrencia.enums';
import { AgendamentoDisparoComponent } from '../agendamento-disparo/agendamento-disparo.component';
import { MatTooltip } from '@angular/material/tooltip';
import { TimePickerPadraoComponent } from '../../../../shared/components/time-picker-padrao/time-picker-padrao.component';

export type TipoProgramacaoDisparo = 'Padrão' | 'Recorrente';

@Component({
  selector: 'app-programacao-disparo-form',
  standalone: true,
  imports: [
    SelectOneComponent,
    FormFieldComponent,
    AgendamentoDisparoComponent,
    DatePickerPadraoComponent,
    DiasSemanaComponent,
    MatTooltip,
    TimePickerPadraoComponent
  ],
  templateUrl: './programacao-disparo-form.component.html',
  styleUrl: './programacao-disparo-form.component.css'
})
export class ProgramacaoDisparoFormComponent implements OnChanges {
  @Input() tipoProgramacao: TipoProgramacaoDisparo = 'Padrão';
  @Output() tipoProgramacaoChange = new EventEmitter<TipoProgramacaoDisparo>();

  @Input() programacao: ProgramacaoDisparoModel = new ProgramacaoDisparoModel();
  @Output() programacaoChange = new EventEmitter<ProgramacaoDisparoModel>();

  @Input() dataAgendamento?: Date;
  @Input() horaAgendamento?: string;
  @Output() agendamentoChange = new EventEmitter<{ data: unknown; hora: string }>();

  @Input() disabled = false;

  readonly tiposProgramacao: TipoProgramacaoDisparo[] = ['Padrão', 'Recorrente'];
  readonly frequencias = FREQUENCIAS.filter(opcao => opcao.valor !== 'Personalizada');
  readonly terminos = TERMINOS;
  readonly tiposMensais = TIPO_RECORRENCIA_MENSAL;
  readonly posicoes = POSICOES_SEMANA_MES;
  readonly diasSemanaMes = DIAS_SEMANA_MES;
  readonly unidadesPersonalizadas = UNIDADES_PERSONALIZADA;

  dataInicioSelecionada: Moment | null = null;
  dataTerminoSelecionada: Moment | null = null;
  readonly dataMinimaInicio = moment().startOf('day');
  private dataReferenciaGeracao: Moment | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['programacao']) {
      const dataRecebida = this.normalizarDataReferencia(this.programacao?.dataInicio);
      if (!this.dataReferenciaGeracao
          || !this.dataInicioSelecionada
          || !dataRecebida.isSame(this.dataInicioSelecionada, 'day')) {
        this.dataReferenciaGeracao = dataRecebida.clone();
      }
      this.dataInicioSelecionada = dataRecebida;
      this.dataTerminoSelecionada = this.paraMoment(this.programacao.dataTermino);
      this.aplicarPadroes();
    }
  }

  selecionarTipoProgramacao(tipo: TipoProgramacaoDisparo): void {
    this.tipoProgramacao = tipo ?? 'Padrão';
    if (this.tipoProgramacao === 'Recorrente') {
      this.aplicarPadroes();
      this.emitirProgramacao();
    }
    this.tipoProgramacaoChange.emit(this.tipoProgramacao);
  }

  selecionarFrequencia(frequencia: FrequenciaRecorrencia): void {
    this.programacao.frequencia = frequencia;
    this.programacao.intervalo = 1;
    this.programacao.diasSemana = [];
    this.programacao.tipoRecorrenciaMensal = undefined;
    this.programacao.diaMes = undefined;
    this.programacao.ultimoDiaMes = undefined;
    this.programacao.posicaoSemanaMes = undefined;
    this.programacao.diaSemanaMes = undefined;
    this.programacao.unidadePersonalizada = undefined;

    if (frequencia === 'Mensal') {
      this.programacao.tipoRecorrenciaMensal = this.tiposMensais.DIA_DO_MES;
      this.programacao.diaMes = 1;
      this.programacao.ultimoDiaMes = false;
    } else if (frequencia === 'Personalizada') {
      this.programacao.unidadePersonalizada = 'Dia';
    }

    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  selecionarTipoMensal(tipo: TipoRecorrenciaMensal): void {
    this.programacao.tipoRecorrenciaMensal = tipo;
    if (tipo === this.tiposMensais.DIA_DO_MES) {
      this.programacao.diaMes = this.programacao.diaMes ?? 1;
      this.programacao.ultimoDiaMes = false;
      this.programacao.posicaoSemanaMes = undefined;
      this.programacao.diaSemanaMes = undefined;
    } else {
      this.programacao.diaMes = undefined;
      this.programacao.ultimoDiaMes = undefined;
      this.programacao.posicaoSemanaMes = this.programacao.posicaoSemanaMes ?? 'Primeira';
      this.programacao.diaSemanaMes = this.programacao.diaSemanaMes ?? DiaSemana.SEGUNDA;
    }
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  selecionarTermino(tipo: TipoTerminoRecorrencia): void {
    this.programacao.tipoTermino = tipo;
    this.programacao.dataTermino = undefined;
    this.programacao.numeroEnviosTermino = undefined;
    this.dataTerminoSelecionada = null;
    if (tipo === 'N. de Envios') {
      this.programacao.numeroEnviosTermino = 1;
    }
    this.emitirProgramacao();
  }

  ajustarValor(campo: 'intervalo' | 'diaMes' | 'numeroEnviosTermino', incremento: number, maximo?: number): void {
    const atual = Number(this.programacao[campo] ?? 1);
    const valor = Math.max(1, maximo ? Math.min(maximo, atual + incremento) : atual + incremento);
    this.programacao[campo] = valor;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  atualizarValor(campo: 'intervalo' | 'diaMes' | 'numeroEnviosTermino', valor: number, maximo?: number): void {
    const normalizado = Math.max(1, maximo ? Math.min(maximo, Number(valor) || 1) : Number(valor) || 1);
    this.programacao[campo] = normalizado;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  atualizarDiasSemana(dias: DiaSemana[]): void {
    this.programacao.diasSemana = [...dias];
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  alternarUltimoDiaMes(marcado: boolean): void {
    this.programacao.ultimoDiaMes = marcado;
    this.programacao.diaMes = marcado ? undefined : 1;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  atualizarPosicao(posicao: PosicaoSemanaMes): void {
    this.programacao.posicaoSemanaMes = posicao;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  atualizarDiaSemanaMes(dia: number): void {
    this.programacao.diaSemanaMes = Number(dia);
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  atualizarUnidadePersonalizada(unidade: UnidadePersonalizada): void {
    this.programacao.unidadePersonalizada = unidade;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  obterPosicaoSelecionada(): OpcaoSegmento<PosicaoSemanaMes> | undefined {
    return this.posicoes.find(opcao => opcao.valor === this.programacao.posicaoSemanaMes);
  }

  obterDiaSemanaSelecionado(): OpcaoSegmento<number> | undefined {
    return this.diasSemanaMes.find(opcao => opcao.valor === this.programacao.diaSemanaMes);
  }

  obterUnidadeSelecionada(): OpcaoSegmento<UnidadePersonalizada> | undefined {
    return this.unidadesPersonalizadas.find(opcao => opcao.valor === this.programacao.unidadePersonalizada);
  }

  atualizarAgendamentoPadrao(evento: { data: unknown; hora: string }): void {
    this.agendamentoChange.emit(evento);
  }

  atualizarInicio(evento: { data: unknown; hora: string; dataAlterada?: boolean }): void {
    const dataInformada = this.paraMoment(evento.data)?.startOf('day');
    if (dataInformada && (evento.dataAlterada
        || !this.dataInicioSelecionada
        || !dataInformada.isSame(this.dataInicioSelecionada, 'day'))) {
      this.dataReferenciaGeracao = this.normalizarDataReferencia(dataInformada);
    }
    this.programacao.hora = evento.hora?.replace(/\s/g, '') || undefined;
    const horarios = this.horariosDiarios;
    horarios[0] = this.programacao.hora ?? '';
    this.programacao.horarios = horarios;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  get horariosDiarios(): string[] {
    const horarios = [...(this.programacao.horarios ?? [])];
    if (!horarios.length) horarios.push(this.programacao.hora ?? '');
    return horarios;
  }

  ajustarQuantidadeHorarios(incremento: number): void {
    const horarios = this.horariosDiarios;
    const quantidade = Math.max(1, Math.min(12, horarios.length + incremento));
    while (horarios.length < quantidade) horarios.push('');
    this.programacao.horarios = horarios.slice(0, quantidade);
    this.programacao.hora = this.programacao.horarios[0] || undefined;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  atualizarHorarioDiario(indice: number, horario: string): void {
    const horarios = this.horariosDiarios;
    horarios[indice] = horario?.replace(/\s/g, '') ?? '';
    this.programacao.horarios = horarios;
    this.programacao.hora = horarios[0] || undefined;
    this.recalcularDataProximaGeracao();
    this.emitirProgramacao();
  }

  atualizarDataTermino(data: unknown): void {
    this.dataTerminoSelecionada = this.paraMoment(data);
    this.programacao.dataTermino = this.formatarData(data);
    this.emitirProgramacao();
  }

  private aplicarPadroes(): void {
    this.programacao ??= new ProgramacaoDisparoModel();
    this.programacao.frequencia ??= 'Diário';
    this.programacao.intervalo = Math.max(1, this.programacao.intervalo ?? 1);
    this.programacao.tipoTermino ??= 'Nunca';
    this.programacao.diasSemana ??= [];
    this.programacao.dataInicio ??= this.dataMinimaInicio.format('YYYY-MM-DD');
    this.dataInicioSelecionada ??= this.paraMoment(this.programacao.dataInicio);

    if (this.programacao.frequencia === 'Mensal' && !this.programacao.tipoRecorrenciaMensal) {
      this.programacao.tipoRecorrenciaMensal = this.tiposMensais.DIA_DO_MES;
      this.programacao.diaMes = 1;
      this.programacao.ultimoDiaMes = false;
    }
    if (this.programacao.frequencia === 'Personalizada') {
      this.programacao.unidadePersonalizada ??= 'Dia';
    }
    this.programacao.horarios = this.programacao.horarios?.length
      ? [...this.programacao.horarios]
      : [this.programacao.hora ?? ''];
    this.recalcularDataProximaGeracao();
  }

  private recalcularDataProximaGeracao(): void {
    if (this.tipoProgramacao !== 'Recorrente' || !this.programacao.frequencia) return;
    const agora = moment();
    const referencia = this.normalizarDataReferencia(this.dataReferenciaGeracao);
    let proxima = referencia.clone();

    if (this.programacao.frequencia === 'Diário') {
      const horariosValidos = this.horariosDiarios
        .map(horario => moment(horario.replace(/\s/g, ''), 'HH:mm', true))
        .filter(horario => horario.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());
      const referenciaEhHoje = referencia.isSame(agora, 'day');
      const aindaHaExecucaoNaReferencia = !referenciaEhHoje || horariosValidos.some(horario =>
        !referencia.clone().hour(horario.hour()).minute(horario.minute()).second(0).isBefore(agora, 'minute'));
      if (horariosValidos.length && !aindaHaExecucaoNaReferencia) {
        proxima.add(Math.max(1, this.programacao.intervalo ?? 1), 'day');
      }
    } else if (this.programacao.frequencia === 'Semanal' && this.programacao.diasSemana?.length) {
      for (let deslocamento = 0; deslocamento < 14; deslocamento++) {
        const candidata = referencia.clone().add(deslocamento, 'day');
        if (this.programacao.diasSemana.includes(candidata.isoWeekday() - 1)
            && !this.aplicarHorario(candidata).isBefore(agora, 'minute')) {
          proxima = candidata;
          break;
        }
      }
    } else if (this.programacao.frequencia === 'Mensal') {
      proxima = this.proximaDataMensal(referencia, agora);
    } else if (this.programacao.frequencia === 'Personalizada'
        && this.aplicarHorario(proxima).isBefore(agora, 'minute')) {
      const unidade = this.programacao.unidadePersonalizada ?? 'Dia';
      const unidadeMoment = unidade === 'Semana' ? 'week' : unidade === 'Mês' ? 'month' : 'day';
      proxima.add(Math.max(1, this.programacao.intervalo ?? 1), unidadeMoment);
    }

    this.programacao.dataInicio = proxima.format('YYYY-MM-DD');
    this.dataInicioSelecionada = proxima;
  }

  private proximaDataMensal(referencia: Moment, agora: Moment): Moment {
    for (let meses = 0; meses < 24; meses++) {
      const mes = referencia.clone().startOf('month').add(meses, 'month');
      let candidata: Moment;
      if (this.programacao.tipoRecorrenciaMensal === this.tiposMensais.DIA_DA_SEMANA) {
        const diaSemana = (this.programacao.diaSemanaMes ?? 0) + 1;
        const posicoes: Record<PosicaoSemanaMes, number> = {
          Primeira: 1, Segunda: 2, Terceira: 3, Quarta: 4, Quinta: 5, Última: -1
        };
        const posicao = posicoes[this.programacao.posicaoSemanaMes ?? 'Primeira'];
        if (posicao === -1) {
          const fimMes = mes.clone().endOf('month');
          candidata = fimMes.clone().isoWeekday(diaSemana);
          if (candidata.isAfter(fimMes)) candidata.subtract(7, 'day');
        } else {
          candidata = mes.clone().startOf('month').isoWeekday(diaSemana);
          if (candidata.month() !== mes.month()) candidata.add(7, 'day');
          candidata.add(posicao - 1, 'week');
        }
        if (candidata.month() !== mes.month()) continue;
      } else {
        const dia = this.programacao.ultimoDiaMes
          ? mes.daysInMonth()
          : Math.min(this.programacao.diaMes ?? 1, mes.daysInMonth());
        candidata = mes.clone().date(dia);
      }
      if (!candidata.isBefore(referencia, 'day')
          && !this.aplicarHorario(candidata).isBefore(agora, 'minute')) {
        return candidata.startOf('day');
      }
    }
    return referencia.clone();
  }

  private normalizarDataReferencia(valor: unknown): Moment {
    const data = this.paraMoment(valor)?.startOf('day') ?? this.dataMinimaInicio.clone();
    return data.isBefore(this.dataMinimaInicio, 'day') ? this.dataMinimaInicio.clone() : data;
  }

  private aplicarHorario(data: Moment): Moment {
    const horario = moment((this.programacao.hora ?? '').replace(/\s/g, ''), 'HH:mm', true);
    return horario.isValid()
      ? data.clone().hour(horario.hour()).minute(horario.minute()).second(0)
      : data.clone().endOf('day');
  }

  private emitirProgramacao(): void {
    this.programacaoChange.emit(Object.assign(new ProgramacaoDisparoModel(), this.programacao, {
      diasSemana: [...(this.programacao.diasSemana ?? [])],
      horarios: [...(this.programacao.horarios ?? [])]
    }));
  }

  private paraMoment(valor: unknown): Moment | null {
    if (!valor) {
      return null;
    }
    const data = moment.isMoment(valor) ? valor.clone() : moment(valor);
    return data.isValid() ? data : null;
  }

  private formatarData(valor: unknown): string | undefined {
    return this.paraMoment(valor)?.format('YYYY-MM-DD');
  }
}
